import { useMsal } from "@azure/msal-react";
import { Configuration } from "@azure/msal-browser";
import config from '../Config/environments';

export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  status?: number;
}
export interface LeaveDay{
  date: string;
  code: number;
}

export const msalConfig: Configuration = {
  auth: {
    clientId: "491f0b99-8aa8-4bbf-8247-5621a11a4e93", // This is the ONLY mandatory field that you need to supply.
    authority:
      "https://login.microsoftonline.com/e91a9e2f-0ba3-43ac-8005-550f20b326fe", // Use /organizations for work/school accounts or replace with your specific tenant ID
    redirectUri: "http://localhost:3000", // Points to window.location.origin by default. This must be registered as a redirect URI in your app registration.
    postLogoutRedirectUri: "http://localhost:3000", // Points to window.location.origin by default.
  },
  cache: {
    cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case 0: // LogLevel.Error
            console.error(message);
            return;
          case 1: // LogLevel.Warning
            console.warn(message);
            return;
          case 2: // LogLevel.Info
            console.info(message);
            return;
          case 3: // LogLevel.Verbose
            console.debug(message);
            return;
        }
      },
    },
  },
};


export const useFerieApi = () => {
  const { instance } = useMsal();
  const baseUrl = config.apiBaseUrl;
  const getAccessToken = async (): Promise<string> => {
    const accounts = instance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error("No account logged in");
    }
    try {
      // Try to get token silently first
      const response = await instance.acquireTokenSilent({
        scopes: [`api://${msalConfig.auth.clientId}/access_as_user`],
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      if ((error as any).name === "InteractionRequiredAuthError") {
        const response = await instance.acquireTokenPopup({
          scopes: [`api://${msalConfig.auth.clientId}/access_as_user`],
          account: accounts[0],
        });
        return response.accessToken;
      }
    }
    throw new Error("Failed to acquire access token");
  };
  const makeAuthenticatedRequest = async <T = any>(
    endpoint: string,
    verb: string = 'GET',
    body?: any,
): Promise<ApiResponse<T>> => {
    try {
        const accessToken = await getAccessToken();
        let init:RequestInit = {
           method: verb,
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            }
          };
          if(body) {
            init = { ...init, body: JSON.stringify(body) };
          }
        const response = await fetch(`${baseUrl}${endpoint}`,init);

        if (!response.ok) {
          console.log('Response not ok:', response);
            return {
                error: `API call failed: ${response.status} ${response.statusText}`,
                status: response.status
            };
        }

        const data = await response.json();
        return {
            data,
            status: response.status
        };

    } catch (error) {
        return {
            error: (error as Error).message || 'Unknown error occurred'
        };
    }
};

  const isUser = async (): Promise<boolean|undefined> => {
    const response = await makeAuthenticatedRequest<boolean>('isUser');
    return response.data;
  };
  const getVacation = async (year:number): Promise<Array<LeaveDay>|undefined> => {
    const response = await makeAuthenticatedRequest<Array<LeaveDay>>('vacation/'+year);
    return response.data;
  };
  const toggleDay = async (date: Date, updated: boolean, code: number): Promise<LeaveDay[]| undefined> => {
    const response = await makeAuthenticatedRequest<LeaveDay[]>(`${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}?q=${updated}&c=${code}`,"PUT");
    return response.data;
  }
  const toggleDays = async (dates: string[], toggleMode:ToggleMode, year:number, activityCode:number): Promise<LeaveDay[]| undefined> => {
    const response = await makeAuthenticatedRequest<LeaveDay[]>(`toggleDays`, "POST",{ dates, mode:toggleMode, year, activityCode });
    return response.data;
  }
  const getActivityTypes = async (): Promise<Array<{code:number, name:string}>|undefined> => {
    const response = await makeAuthenticatedRequest<Array<{code:number, name:string}>>('activityTypes');
    return response.data;
  }
 enum ToggleMode {
    On,Off
  }  

  return {
    isUser,
    getVacation,
    toggleDay,
    toggleDays,
    getActivityTypes,
    ToggleMode
  };
  
};
